import React from 'react';
import { connect } from 'react-redux';
import Checkbox from '@mui/material/Checkbox';
import { acknowledgeIncomingTicket } from '../../../services/ticketsService';
import { closeModal, openModal } from '../../../store/actions/errorModalActions';
import { openSnackbar } from '../../../store/actions/snackbarActions';
import {
  CellInfo,
  ColumnProjection,
  GetColumnsProps,
  RowInfo,
} from '../../shared/tickets/ticketListConfigurations/ticketListConfigurations.types';
import listStyles from '../../shared/tickets/TicketList.module.css';
import { TicketWithCurrentStatusDto } from '../../../types';
import { canAcknowledgeMaterialReceipt } from '../../../util/statusesUtil';
import {
  MaterialReceivedActionProps,
  openMaterialReceivedConfirm,
} from './materialReceivedConfirm';

export interface MaterialReceivedCellProps extends MaterialReceivedActionProps {
  ticket: TicketWithCurrentStatusDto;
  /** Edit-level access to the IncomingMaterials flag; View-only users see state but cannot act. */
  hasEditAccess: boolean;
}

/**
 * DEV-4153: "Received" column cell for the Incoming Materials ticket lists.
 * Acknowledged tickets keep the checked, locked checkbox even when voided —
 * the acknowledgement is a recorded fact and there is no un-acknowledge — with
 * the shared voided styling for row consistency. Tickets that cannot be
 * acknowledged (not yet OnJob per the epic's "past To Job", voided, or viewed
 * without Edit access to the IncomingMaterials flag) render a placeholder
 * dash; everything else gets the actionable checkbox behind the confirm modal.
 */
export function MaterialReceivedCell(props: MaterialReceivedCellProps) {
  const { ticket } = props;
  const isAcknowledged = !!ticket.materialReceivedAt;
  const canAcknowledge = canAcknowledgeMaterialReceipt(ticket, props.hasEditAccess);

  // The row's own click handler opens the ticket drill-down; interacting with
  // the checkbox must not navigate.
  const stopRowClick = (event: React.MouseEvent) => event.stopPropagation();

  if (!isAcknowledged && !canAcknowledge) {
    return (
      <span
        className={ticket.voided ? listStyles.voidedCellStyle : undefined}
        data-test="material-received-placeholder"
      >
        -
      </span>
    );
  }

  const openConfirm = (event: React.MouseEvent) => {
    stopRowClick(event);
    openMaterialReceivedConfirm(ticket.id, props);
  };

  return (
    <span
      className={ticket.voided ? listStyles.voidedCellStyle : undefined}
      data-test="material-received-cell"
      onClick={isAcknowledged ? stopRowClick : openConfirm}
      role="presentation"
    >
      <Checkbox
        checked={isAcknowledged}
        disabled={isAcknowledged}
        inputProps={{
          'aria-label': isAcknowledged
            ? `Material received for ticket ${ticket.ticketNumber}`
            : `Mark ticket ${ticket.ticketNumber} as received`,
        }}
        size="small"
      />
    </span>
  );
}

const ConnectedMaterialReceivedCell = connect(null, {
  openModal,
  closeModal,
  openSnackbar,
  acknowledgeIncomingTicket,
})(MaterialReceivedCell);

export default ConnectedMaterialReceivedCell;

// DEV-4153: sort value for the Received column — acknowledged tickets group by
// their acknowledgement time, unacknowledged sort together as ''.
export const materialReceivedAtAccessor = (row: RowInfo) => row.materialReceivedAt ?? '';

/**
 * DEV-4153: the Received column, shared by the current and past incoming
 * configs — id/accessor/Cell must stay identical on both lists for the shared
 * cell and shared sort to behave the same; width is the only per-list
 * variation. Past tickets carry no currentStatus, so the cell naturally
 * renders read-only there. `hasEditAccess` is a page-level fact computed once
 * per list render and threaded through GetColumnsProps — the cell holds no
 * store subscription of its own.
 */
export const receivedColumn = (
  options: Pick<GetColumnsProps, 'cellStyle' | 'headerStyle'> & {
    width: number;
    hasEditAccess: boolean;
  },
): ColumnProjection => ({
  Header: 'Received',
  id: 'received',
  accessor: materialReceivedAtAccessor,
  Cell: (cellInfo: CellInfo) =>
    cellInfo.original ? (
      <ConnectedMaterialReceivedCell
        ticket={cellInfo.original}
        hasEditAccess={options.hasEditAccess}
      />
    ) : (
      ''
    ),
  className: 'ticket-cell',
  headerClassName: 'ticket-header',
  style: options.cellStyle,
  width: options.width,
  headerStyle: options.headerStyle,
});
